import { Suspense, lazy, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate, Route, Routes } from "react-router-dom";
import { useIntl } from "react-intl";
import { styled } from "@mui/material";

import { userLogouttoUserDashboard } from "auth/helpers/authApi";
import { UserPreferredLocale } from "auth/types";

import { useAuthUser } from "context/GameAuthContext";

import { CombinedProvider } from "utils/contexts/combined/user-dashboard";

import { useLocalizationContext } from "localization/LocalizationProvider";

import { fetchUserFunnel } from "user-dashboard/utils/fetch";
import FloatingChatWithAI from "user-dashboard/components/chat-ai/FloatingChatWithAI";

import { isFeatureEnabled } from "feature-configs/featureConfigUtils";
import { FeatureConfig } from "feature-configs/featureConfigApi";

import Header from "../components/header/Header";
import "../index.css";

const UserJourney = lazy(() => import("user-journey/UserJourney"));
const BuddyDuty = lazy(() => import("user-journey/BuddyDuty"));

interface organizationData {
	companyLogo: string;
	organizationName: string;
	theme: any;
}

const Container = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",

	padding: "0 3.2rem",
	backgroundColor: theme.palette.legacyPrimary["100"],
}));

const UserDashboardScreen = () => {
	const user = useAuthUser();
	const intl = useIntl();
	const { updateTranslations } = useLocalizationContext();

	const [serverOrganizationData, setServerOrganizationData] = useState<organizationData | undefined>(undefined);

	useEffect(() => {
		async function fetch() {
			const response = await fetchUserFunnel();
			const { headerDataObj } = response;
			setServerOrganizationData(headerDataObj as organizationData);
		}
		fetch();
		userLogouttoUserDashboard();
	}, []);

	useEffect(() => {
		updateTranslations({ locale: user.preferredLocale || UserPreferredLocale.NL });
	}, [updateTranslations, user.preferredLocale]);

	return (
		<div id="user-dashboard">
			<Helmet>
				<title>{intl.formatMessage({ id: "USER_DASHBOARD.page.title" })}</title>
			</Helmet>
			{serverOrganizationData && (
				<CombinedProvider>
					<Header username={user.name.split(" ")[0]} companyLogo={serverOrganizationData.companyLogo} />
					<Container className="user-journey-wrapper">
						<Suspense fallback={null}>
							<Routes>
								<Route path="/" element={<UserJourney />} />
								{isFeatureEnabled(FeatureConfig.buddy) && (
									<Route path="/buddy" element={<BuddyDuty />} />
								)}
								<Route path="*" element={<Navigate to="/user-dashboard" />} />
							</Routes>
						</Suspense>
					</Container>
					{isFeatureEnabled(FeatureConfig.knowledgeSources) && (
						<FloatingChatWithAI
							organizationName={serverOrganizationData.organizationName}
							logo={serverOrganizationData.companyLogo}
						/>
					)}
				</CombinedProvider>
			)}
		</div>
	);
};

export default UserDashboardScreen;
